/**
 * Contacts — ALL contacts across all participant roles (Module 2 nav, 2026-06-17).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  AD-008/009 FIRST-CLASS NAV (module2-contacts-nav):
 *  The `clients` Firestore collection IS the contacts collection — the name
 *  is a historical artefact. Every person in this system, regardless of their
 *  project role, is a Contact. This screen makes that explicit.
 * ─────────────────────────────────────────────────────────────────────────
 *
 *  FILTER TABS: All · Clients · Partners · Consultants · Family · Staff
 *  Each tab filters the full contact list by the roles those contacts play
 *  across project participants[]. The "Clients" tab also catches legacy
 *  projects where the clientId field on the project matches the contact id.
 *
 *  DEDUP BANNER: moved here from Clients.tsx (kept in Clients.tsx too — the
 *  same sessionStorage key so dismissing here also dismisses there).
 *
 *  "+" New Contact — navigates to the same create path as before.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Btn3, Card, EmptyNote, ListScreen } from '../components/ui';
import { ListLoading } from '../components/ListState';
import { DEMO_STAFF, inr, seedProjects } from '../lib/trackDemo';
import {
  clientRowsFromProjects,
  contactStatus,
  createClient,
  listenClients,
  listenProjects,
  markContactsNotDuplicate,
  matchesClientRow,
  mergeClientRows,
  savePhones,
  saveEmails,
  saveRoleTags,
  savePersonData,
  trashClient,
  updateClient,
  type ClientRow,
  type LiveClient,
  type LiveProject,
} from '../lib/projectStore';
import {
  listenFamilies,
  markGroupsNotDuplicate,
  mergeFamilyInto,
  trashFamily,
  type FamilyGroup,
} from '../lib/familyStore';
import {
  mergeContactInto,
  reconcileDuplicateContacts,
  type ReconcilePlan,
} from '../lib/contactMerge';
import { phoneKey, emailKey, nameTokens } from '../lib/contactMatch';
import { primaryPhone, primaryEmail, pobDisplayName } from '../lib/contactModel';
import {
  enrichContactRoles,
  lensBadges,
  lensRolesForContactTags,
  ASSIGNABLE_CONTACT_ROLES,
  type P1LensRole,
  type ProjectRole,
  type GroupMembershipLite,
} from '../lib/p1Roles';

// ─── filter tab types ─────────────────────────────────────────────────────
//
// P1 §6 — CURATED visible lenses (spec §12 open-Q2 recommended default): a small
// tab set that keeps the directory uncluttered — All · Clients · Family ·
// Professionals · Organisations — with the remaining roles (Partners /
// Consultants / AV Staff) reachable through the orthogonal "filter by role"
// control below. Every lens is driven by the reconciled 3-axis role set
// (Build K), so professionals and organisation members are no longer invisible.

type ContactTab =
  | 'all'
  | 'clients'
  | 'family'
  | 'professionals'
  | 'organisations'
  | 'duplicates';

const TAB_KEYS: ContactTab[] = [
  'all',
  'clients',
  'family',
  'professionals',
  'organisations',
  'duplicates',
];
const TAB_LABEL_MAP: Record<ContactTab, string> = {
  all: 'All',
  clients: 'Clients',
  family: 'Family',
  professionals: 'Professionals',
  organisations: 'Organisations',
  duplicates: 'Duplicates',
};

// PERSON tabs render person cards. GROUP tabs (family / organisations) render
// the GROUP CARDS themselves, and Duplicates renders the resolve panel — both
// bypass the people-tab filter machinery (matchesTab / tabFiltered / list).
const PERSON_TABS: ReadonlySet<ContactTab> = new Set([
  'all',
  'clients',
  'professionals',
]);

// "Filter by role" — the roles that don't warrant their own tab, applied as an
// additional (intersection) filter on top of the active tab. This is the
// "filter by any role" control from §6, kept compact.
type RoleFilter = '' | 'partner' | 'consultant' | 'staff';

const ROLE_FILTER_OPTIONS: { value: RoleFilter; label: string }[] = [
  { value: '', label: 'Any role' },
  { value: 'partner', label: 'Partners' },
  { value: 'consultant', label: 'Consultants' },
  { value: 'staff', label: 'AV Staff' },
];

// ─── role-resolution helpers ──────────────────────────────────────────────

/**
 * PURE — given all live projects and a contact id, return the set of ROLE/LENS
 * markers that contact wears across the app.
 *
 * Build K (2026-07-06) — reconciles the two role systems. The base set is still
 * the project-participation roles (`participants[].role`, R4) with the legacy
 * clientId / partnerId fall-backs — unchanged, so every existing path behaves
 * exactly as before. On top, `enrichContactRoles` folds in the derived lenses
 * from the contact's DURABLE group memberships (`org_member` / `family_member` /
 * `professional`), so professionals and organisation members are no longer
 * invisible to the directory. `groups` defaults to none, so a caller that only
 * has projects still gets the pure project role set.
 */
function rolesForContact(
  contactId: string,
  projects: LiveProject[],
  groups: readonly GroupMembershipLite[] = [],
  directRoles: readonly P1LensRole[] = [],
): Set<P1LensRole> {
  const base = new Set<ProjectRole>();
  for (const p of projects) {
    if (p.participants && p.participants.length > 0) {
      for (const entry of p.participants) {
        if (entry.contactId === contactId) {
          base.add(entry.role);
        }
      }
    } else {
      // Legacy path — project has no participants[] yet
      if ((p.clientId || p.clientNo) === contactId) base.add('legacy_client');
      if (p.partnerId === contactId) base.add('legacy_partner');
    }
  }
  // Build K derivation (project roles + group-membership lenses), then fold in
  // any roles ASSIGNED DIRECTLY on the contact (§6 — roleTags → lens roles), so
  // a hand-assigned professional/partner/etc. surfaces in the matching lens.
  const set = enrichContactRoles(base, groups);
  for (const r of directRoles) set.add(r);
  if (set.has('consultant')) set.add('professional');
  return set;
}

/**
 * PURE — does the given role-set match the active tab?
 *
 * For the 'family' tab:
 *   Primary filter: contacts whose `familyIds?.length > 0` (belong to any
 *   FamilyGroup of type 'family').
 *   Fallback: also include contacts with participant role=family on any
 *   project (existing behaviour — kept on top).
 */
function matchesTab(
  tab: ContactTab,
  roles: Set<P1LensRole>,
  clientId: string,
  allProjects: LiveProject[],
  familyGroupIds?: Set<string>,  // IDs of 'family'-type groups this contact belongs to
): boolean {
  if (tab === 'all') return true;

  // Build I — the Clients lens is the shared client-vs-Contact derivation
  // (contactStatus), so this tab and the record header agree on who is a Client.
  if (tab === 'clients') return contactStatus(clientId, allProjects) === 'client';
  // §6 — the Professionals lens: external_professional / consultant / vendor /
  // contractor, reconciled from the 3-axis personModel vocab AND from a role
  // assigned directly on the contact (Build K + roleTags).
  if (tab === 'professionals') return roles.has('professional') || roles.has('consultant');
  // §6 — the Organisations lens: contacts who belong to an organisation-type
  // group (org members surface here; org-type Groups were invisible before).
  if (tab === 'organisations') return roles.has('org_member');
  if (tab === 'family') {
    // Primary filter — belongs to a family-type Group (Module 2 Groups feature)
    if (familyGroupIds && familyGroupIds.size > 0) return true;
    // Build K — the derived family_member lens (any family-type membership) plus
    // the legacy fall-back (participant role=family or co_client on any project).
    return roles.has('family_member') || roles.has('family') || roles.has('co_client');
  }
  return true;
}

/**
 * PURE — does the role-set satisfy the "filter by role" control (the roles that
 * don't have their own tab)? An empty filter matches everything. Applied as an
 * intersection on top of the active tab.
 */
function matchesRoleFilter(
  rf: RoleFilter,
  roles: Set<P1LensRole>,
  clientId: string,
  allProjects: LiveProject[],
): boolean {
  if (rf === '') return true;
  if (rf === 'partner') {
    if (roles.has('partner') || roles.has('legacy_partner')) return true;
    return allProjects.some(
      (p) => !p.participants?.length && p.partnerId === clientId,
    );
  }
  if (rf === 'consultant') return roles.has('consultant');
  if (rf === 'staff') return roles.has('staff');
  return true;
}

// Build K — role badges are the shared derived-lens badges (`lensBadges` in
// lib/p1Roles), so the directory row reflects the reconciled roles (including
// the Professional / Organisation lenses), not only the R4 project roles.

// ─── presentational helpers ───────────────────────────────────────────────

/** Two-letter initials for the circular person avatar. */
function initials(name: string): string {
  const parts = name.replace(/^(ar|dr|mr|mrs|ms)\.?\s+/i, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** All normalised phone keys for a contact (legacy + array). */
function allPhoneKeys(c: LiveClient): string[] {
  return [c.phone, ...(c.phones ?? []).map((p) => p.number)].map(phoneKey).filter(Boolean);
}
/** All normalised email keys for a contact (legacy + array). */
function allEmailKeys(c: LiveClient): string[] {
  return [c.email, ...(c.emails ?? []).map((e) => e.address)].map(emailKey).filter(Boolean);
}

function fmtPhones(c: LiveClient): string {
  const list = [...(c.phones ?? []).map((p) => p.number), c.phone].map((s) => (s ?? '').trim());
  return Array.from(new Set(list.filter(Boolean))).join(', ');
}
function fmtEmails(c: LiveClient): string {
  const list = [...(c.emails ?? []).map((e) => e.address), c.email].map((s) => (s ?? '').trim());
  return Array.from(new Set(list.filter(Boolean))).join(', ');
}
function fmtBirth(c: LiveClient): string {
  const parts = [c.dob, c.tob, c.pob?.name || pobDisplayName(c.pob?.city, c.pob?.state, c.pob?.country)]
    .map((s) => (s ?? '').trim())
    .filter(Boolean);
  return parts.join(' · ');
}
function fmtAddress(c: LiveClient): string {
  const a = c.address;
  if (!a) return '';
  return [a.street, a.city, a.state, a.pin, a.country].map((s) => (s ?? '').trim()).filter(Boolean).join(', ');
}

// ─── DEDUP ENGINE — reuses the pure matcher primitives (contactMatch) ─────
//
// Person duplicates fire on the SAME signals the spine matcher uses (exact
// phone / exact email / token-sorted exact name). Group duplicates fire on a
// same-type name match, or an exact GSTIN match for organisations. Both are
// filtered by the symmetric `notDuplicateOf` suppression so a human's
// "not a duplicate — keep both" is permanent, not a session dismiss.

interface DupReason { key: string; label: string }

/** PURE — why (if at all) are these two contacts a suspected duplicate? */
function personDupReasons(a: LiveClient, b: LiveClient): DupReason[] | null {
  const reasons: DupReason[] = [];
  const aPhones = allPhoneKeys(a), bPhones = allPhoneKeys(b);
  const sharedPhone = aPhones.find((p) => bPhones.includes(p));
  if (sharedPhone) reasons.push({ key: 'phone', label: `same phone ${sharedPhone}` });
  const aEmails = allEmailKeys(a), bEmails = allEmailKeys(b);
  const sharedEmail = aEmails.find((e) => bEmails.includes(e));
  if (sharedEmail) reasons.push({ key: 'email', label: `same email ${sharedEmail}` });
  const an = nameTokens(a.name), bn = nameTokens(b.name);
  if (an && an === bn) reasons.push({ key: 'name', label: 'very similar name' });
  return reasons.length > 0 ? reasons : null;
}

/** PURE — is this pair suppressed by either side's confirmed-distinct list? */
function isSuppressed(aIds: string[] | undefined, bId: string, bIds: string[] | undefined, aId: string): boolean {
  return Boolean(aIds?.includes(bId) || bIds?.includes(aId));
}

interface PersonDupPair { a: LiveClient; b: LiveClient; reasons: DupReason[] }

/** PURE — all suspected person-duplicate pairs (suppressed pairs dropped). */
function buildPersonDupPairs(contacts: LiveClient[]): PersonDupPair[] {
  const live = contacts.filter((c) => !c.trashed);
  const out: PersonDupPair[] = [];
  for (let i = 0; i < live.length; i++) {
    for (let j = i + 1; j < live.length; j++) {
      const a = live[i], b = live[j];
      if (isSuppressed(a.notDuplicateOf, b.cid, b.notDuplicateOf, a.cid)) continue;
      const reasons = personDupReasons(a, b);
      if (reasons) out.push({ a, b, reasons });
    }
  }
  return out;
}

interface GroupDupPair { a: FamilyGroup; b: FamilyGroup; reasons: DupReason[]; kind: 'family' | 'org' }

/** PURE — why (if at all) are these two same-type groups a suspected duplicate? */
function groupDupReasons(a: FamilyGroup, b: FamilyGroup): DupReason[] | null {
  const reasons: DupReason[] = [];
  const an = nameTokens(a.name), bn = nameTokens(b.name);
  if (an && an === bn) reasons.push({ key: 'name', label: 'very similar name' });
  if (a.type === 'organization') {
    const ag = (a.gstNumber ?? '').trim().toUpperCase();
    const bg = (b.gstNumber ?? '').trim().toUpperCase();
    if (ag && ag === bg) reasons.push({ key: 'gstin', label: `same GSTIN ${ag}` });
  }
  return reasons.length > 0 ? reasons : null;
}

/** PURE — all suspected group-duplicate pairs (families and orgs, suppressed dropped). */
function buildGroupDupPairs(groups: FamilyGroup[]): GroupDupPair[] {
  const out: GroupDupPair[] = [];
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      const a = groups[i], b = groups[j];
      if (a.type !== b.type) continue;
      if (isSuppressed(a.notDuplicateOf, b.id, b.notDuplicateOf, a.id)) continue;
      const reasons = groupDupReasons(a, b);
      if (reasons) out.push({ a, b, reasons, kind: a.type === 'organization' ? 'org' : 'family' });
    }
  }
  return out;
}

// ─── demo adapter ──────────────────────────────────────────────────────────

function demoToLive(p: ReturnType<typeof seedProjects>[number]): LiveProject {
  return {
    pid: p.pid,
    vp: p.vp,
    projectName: p.projectName ?? '',
    clientName: p.clientName,
    clientNo: p.clientNo,
    clientId: p.clientNo,
    partnerId: null,
    tierCode: p.tier,
    tier: p.tier,
    activeStageKey: p.activeStageKey,
    assigneeUid: p.assigneeUid,
    assigneeName: p.assigneeName,
    valueINR: p.valueINR,
    clearedGateIds: p.clearedGateIds,
    archived: false,
    trashed: false,
    trashedAt: null,
    trashedBy: null,
    paymentMode: null,
    currency: 'INR',
    firmId: null,
    gst: null,
    gateAmounts: {},
    feeHistory: [],
    moneyPaid: {},
    trackedInPayments: false,
  };
}

// ─── search-input style ────────────────────────────────────────────────────

const searchStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '7px 11px',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  minWidth: 220,
  flex: '1 1 220px',
  maxWidth: 360,
};

// ─── input style shared with NewContactForm ────────────────────────────────

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '0 0 8px',
      }}
    >
      {children}
    </div>
  );
}

// ─── main screen ──────────────────────────────────────────────────────────

export default function Contacts() {
  const { role, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const actorName = user?.displayName || user?.email?.split('@')[0] || 'You';

  // Tab: read from ?role= query param (for redirects from /clients, /partners)
  // or use the 'all' default. Legacy role values that no longer have their own
  // tab (partner / consultant / staff) translate into the "filter by role"
  // control so old deep-links still land on the right people.
  const rawTab = searchParams.get('role') ?? 'all';
  const rawRoleFilter = searchParams.get('rolefilter') ?? '';

  const legacyRoleFilter: RoleFilter | null = (() => {
    if (rawTab === 'partner' || rawTab === 'partners') return 'partner';
    if (rawTab === 'consultant' || rawTab === 'consultants') return 'consultant';
    if (rawTab === 'staff') return 'staff';
    return null;
  })();

  const activeTab: ContactTab = (() => {
    if (rawTab === 'client') return 'clients';
    if (legacyRoleFilter) return 'all';
    const found = TAB_KEYS.find((k) => k === rawTab);
    return found ? found : 'all';
  })();

  const roleFilter: RoleFilter = (() => {
    const valid = ROLE_FILTER_OPTIONS.find((o) => o.value === rawRoleFilter);
    if (valid && valid.value !== '') return valid.value;
    return legacyRoleFilter ?? '';
  })();

  function setTab(tab: ContactTab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'all') next.delete('role');
        else next.set('role', tab);
        return next;
      },
      { replace: true },
    );
  }

  function setRoleFilter(rf: RoleFilter) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // Clear any legacy role value that mapped into the filter.
        if (legacyRoleFilter) next.delete('role');
        if (rf === '') next.delete('rolefilter');
        else next.set('rolefilter', rf);
        return next;
      },
      { replace: true },
    );
  }

  const [live, setLive] = useState<LiveProject[]>([]);
  const [masters, setMasters] = useState<LiveClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  // Reconcile astro_sync duplicate shadows (admin tool). Two-step: preview
  // (dryRun) → confirm apply. Never a blind bulk write.
  const [reconcilePlan, setReconcilePlan] = useState<ReconcilePlan | null>(null);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  // Module 2 Groups — family groups (for Family tab primary filter + badge)
  const [familyGroups, setFamilyGroups] = useState<FamilyGroup[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 140);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const unsub = listenProjects(setLive, {
      onErr: () => toast('Could not load contacts (check connection)'),
      onReady: () => setLoading(false),
    });
    return unsub;
  }, [toast]);

  useEffect(() => {
    const unsub = listenClients(setMasters, {
      onErr: () => toast('Could not load contact records (check connection)'),
      onReady: () => setLoading(false),
    });
    return unsub;
  }, [toast]);

  useEffect(() => {
    // Listen to all family groups — used by the Family tab primary filter.
    const unsub = listenFamilies(setFamilyGroups);
    return unsub;
  }, []);

  // All projects (including demo seed)
  const allProjects: LiveProject[] = useMemo(() => {
    const merged = [...live, ...seedProjects().map(demoToLive)];
    return role === 'admin'
      ? merged
      : merged.filter((p) => p.assigneeUid === DEMO_STAFF.uid);
  }, [live, role]);

  // All client rows (all contacts), merging in masters
  const clientRows = useMemo(() => {
    const folded = clientRowsFromProjects(allProjects, includeArchived);
    return role === 'admin'
      ? mergeClientRows(folded, masters, includeArchived)
      : folded;
  }, [allProjects, masters, includeArchived, role]);

  // Build K — map contactId → their durable group memberships (type + the
  // membership relationship label). This is the 3-axis-reconcile input: an
  // organization membership drives the Organisation lens, a family membership
  // the Family lens, and an external-labelled membership the Professionals lens.
  const contactGroupMemberships = useMemo(() => {
    const map = new Map<string, GroupMembershipLite[]>();
    for (const g of familyGroups) {
      for (const m of g.members) {
        const list = map.get(m.contactId) ?? [];
        // Build C — thread the durable org-axis + kinship persisted on the
        // member so enrichContactRoles can sharpen (work_role vendor/contractor
        // → professional), not just the coarse membership type.
        list.push({
          type: g.type,
          relationship: m.relationship,
          work_role: m.work_role,
          relation_to_client: m.relation_to_client,
          department: m.department,
        });
        map.set(m.contactId, list);
      }
    }
    return map;
  }, [familyGroups]);

  // §6 — map contactId → the lens roles ASSIGNED DIRECTLY on the contact
  // (clients/{cid}.roleTags[] → lens roles). Folded into the role set alongside
  // the derived lenses so a hand-assigned professional/partner/etc. surfaces in
  // the matching lens, not just under "All".
  const contactDirectRoles = useMemo(() => {
    const map = new Map<string, P1LensRole[]>();
    for (const m of masters) {
      if (m.roleTags && m.roleTags.length > 0) {
        map.set(m.cid, lensRolesForContactTags(m.roleTags));
      }
    }
    return map;
  }, [masters]);

  // For each contact row, compute their reconciled role set (project roles +
  // the derived group-membership lenses — Build K — + directly-assigned roles).
  const contactsWithRoles = useMemo(() => {
    return clientRows.map((c) => ({
      ...c,
      roles: rolesForContact(
        c.clientId,
        allProjects,
        contactGroupMemberships.get(c.clientId) ?? [],
        contactDirectRoles.get(c.clientId) ?? [],
      ),
    }));
  }, [clientRows, allProjects, contactGroupMemberships, contactDirectRoles]);

  // Module 2 Groups — map contactId → Set of family-type group IDs
  // Used by the Family tab to surface contacts belonging to family groups.
  const contactFamilyGroupIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const g of familyGroups) {
      if (g.type !== 'family') continue;
      for (const m of g.members) {
        if (!map.has(m.contactId)) map.set(m.contactId, new Set());
        map.get(m.contactId)!.add(g.id);
      }
    }
    return map;
  }, [familyGroups]);

  // Apply tab filter + the orthogonal "filter by role" control (intersection).
  const tabFiltered = useMemo(() => {
    if (activeTab === 'all' && roleFilter === '') return contactsWithRoles;
    return contactsWithRoles.filter(
      (c) =>
        matchesTab(
          activeTab,
          c.roles,
          c.clientId,
          allProjects,
          contactFamilyGroupIds.get(c.clientId),
        ) && matchesRoleFilter(roleFilter, c.roles, c.clientId, allProjects),
    );
  }, [contactsWithRoles, activeTab, roleFilter, allProjects, contactFamilyGroupIds]);

  // Apply search filter — also supports VP# search (VP1001, V1001, vp1001 etc.)
  // When the query looks like a VP number, find contacts whose clientId appears
  // on a project whose vp field matches — uses the already-loaded allProjects stream.
  const list = useMemo(() => {
    const vpMatch = /^v(?:p)?(\d+)$/i.exec(query.trim());
    if (vpMatch) {
      // Collect contactIds from matching projects
      const matchingClientIds = new Set<string>();
      for (const p of allProjects) {
        if (p.vp.toLowerCase().includes(query.trim().toLowerCase())) {
          // Primary client via clientId or legacy clientNo
          if (p.clientId) matchingClientIds.add(p.clientId);
          if (p.clientNo) matchingClientIds.add(p.clientNo);
          // Also include all explicit participants
          for (const entry of (p.participants ?? [])) {
            matchingClientIds.add(entry.contactId);
          }
        }
      }
      if (matchingClientIds.size > 0) {
        return tabFiltered.filter((r) => matchingClientIds.has(r.clientId));
      }
      // Fall through to standard search if no VP match found
    }
    return tabFiltered.filter((r) => matchesClientRow(r, query));
  }, [tabFiltered, query, allProjects]);

  // masters keyed by cid — the source of truth for the person cards' phone/
  // email row, the Duplicates compare view, and merge writes.
  const mastersById = useMemo(() => {
    const map = new Map<string, LiveClient>();
    for (const m of masters) map.set(m.cid, m);
    return map;
  }, [masters]);

  // GROUP tabs render the groups themselves. Split the families/ collection by
  // type: family-type → Family tab, organization-type → Organisations tab.
  const familyGroupsList = useMemo(
    () => familyGroups.filter((g) => g.type === 'family').sort((a, b) => a.name.localeCompare(b.name)),
    [familyGroups],
  );
  const orgGroupsList = useMemo(
    () =>
      familyGroups
        .filter((g) => g.type === 'organization')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [familyGroups],
  );

  // DUPLICATES tab — real detection off the loaded contacts + groups, filtered
  // by the symmetric `notDuplicateOf` suppression.
  const personDupPairs = useMemo(() => buildPersonDupPairs(masters), [masters]);
  const groupDupPairs = useMemo(() => buildGroupDupPairs(familyGroups), [familyGroups]);
  const dupCount = personDupPairs.length + groupDupPairs.length;

  // Group-name lookup for a contact (used in the compare view's "Groups" row).
  function groupNamesForContact(cid: string): string {
    return familyGroups
      .filter((g) => g.members.some((m) => m.contactId === cid))
      .map((g) => g.name)
      .join(', ');
  }

  // Per-tab count badges (mockup shows a count on every tab).
  const clientCount = useMemo(
    () => contactsWithRoles.filter((c) => contactStatus(c.clientId, allProjects) === 'client').length,
    [contactsWithRoles, allProjects],
  );
  const professionalCount = useMemo(
    () => contactsWithRoles.filter((c) => c.roles.has('professional') || c.roles.has('consultant')).length,
    [contactsWithRoles],
  );
  const tabCount: Record<ContactTab, number> = {
    all: contactsWithRoles.length,
    clients: clientCount,
    family: familyGroupsList.length,
    professionals: professionalCount,
    organisations: orgGroupsList.length,
    duplicates: dupCount,
  };

  // Reconcile — step 1: compute the plan (dryRun, no writes) and show it.
  async function previewReconcile() {
    setReconcileBusy(true);
    try {
      const plan = await reconcileDuplicateContacts({ dryRun: true, actorName });
      setReconcilePlan(plan);
    } catch {
      toast('Could not compute the reconcile plan (check connection)');
    } finally {
      setReconcileBusy(false);
    }
  }

  // Reconcile — step 2: apply the previewed plan (second confirm).
  async function applyReconcile() {
    setReconcileBusy(true);
    try {
      const applied = await reconcileDuplicateContacts({ dryRun: false, actorName });
      setReconcilePlan(null);
      toast(
        `Reconciled: ${applied.counts.willMerge} merged, ` +
          `${applied.counts.willKeep} kept, ${applied.counts.needsReview} need review`,
      );
    } catch {
      toast('Reconcile failed — nothing was changed');
    } finally {
      setReconcileBusy(false);
    }
  }

  const toolbar = (
    <>
      <Btn3 intent="ink" size="sm" onClick={() => setCreating((c) => !c)}>
        {creating ? 'Close' : '+ New Contact'}
      </Btn3>
      {role === 'admin' && (
        <Btn3
          intent="ghost"
          size="sm"
          onClick={previewReconcile}
          disabled={reconcileBusy}
        >
          {reconcileBusy && !reconcilePlan ? 'Checking…' : 'Reconcile duplicates'}
        </Btn3>
      )}
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search name, phone or C#####…"
        aria-label="Search contacts"
        style={searchStyle}
      />
      {query && (
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          {list.length} match{list.length === 1 ? '' : 'es'}
        </span>
      )}
      <label
        style={{
          fontSize: 12,
          color: 'var(--ink-mute)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        Filter by role
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
          aria-label="Filter contacts by role"
          style={{
            border: '1px solid var(--rule-strong)',
            borderRadius: 'var(--r-sm)',
            padding: '6px 9px',
            fontSize: 13,
            fontFamily: 'inherit',
            color: 'var(--ink)',
            background: 'var(--surface)',
          }}
        >
          {ROLE_FILTER_OPTIONS.map((o) => (
            <option key={o.value || 'any'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label
        style={{
          fontSize: 12,
          color: 'var(--ink-mute)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginLeft: 'auto',
        }}
      >
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.target.checked)}
        />
        Count archived projects
      </label>
    </>
  );

  // Filter tabs — All · Clients · Family · Professionals · Organisations ·
  // Duplicates. Rendered in the PINNED subHead slot so they stay fixed while
  // only the rows scroll. Family / Organisations list the GROUPS; Duplicates is
  // the former dedup banner promoted to its own tab (mockup 2026-07-06).
  const tabsBar = (
    <div
      role="tablist"
      aria-label="Filter contacts"
      style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}
    >
      {TAB_KEYS.map((key) => {
        const label = TAB_LABEL_MAP[key];
        const active = activeTab === key;
        const isDup = key === 'duplicates';
        const count = tabCount[key];
        return (
          <button
            key={key}
            role="tab"
            aria-selected={active}
            onClick={() => setTab(key)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: active
                ? `2.5px solid ${isDup ? 'var(--warn)' : 'var(--accent)'}`
                : '2.5px solid transparent',
              padding: '10px 14px',
              fontSize: 14.5,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 600,
              color: active
                ? isDup
                  ? 'var(--warn-ink)'
                  : 'var(--accent-deep)'
                : 'var(--ink-mute)',
              marginBottom: -1,
              borderRadius: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            {key === 'family' && <span aria-hidden="true">👪</span>}
            {key === 'organisations' && <span aria-hidden="true">🏢</span>}
            {isDup && <span aria-hidden="true">⧉</span>}
            {label}
            {(count > 0 || !isDup) && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 'var(--r-pill)',
                  background: isDup
                    ? 'var(--warn-wash)'
                    : active
                      ? 'var(--accent-wash)'
                      : 'var(--inset-deep)',
                  color: isDup
                    ? 'var(--warn-ink)'
                    : active
                      ? 'var(--accent-deep)'
                      : 'var(--ink-soft)',
                  border: isDup ? '1px solid var(--warn-edge)' : 'none',
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <ListScreen
      title="Contacts"
      note="All contacts, lensed by role — Clients · Family · Professionals · Organisations (use Filter by role for partners, consultants and staff)"
      toolbar={toolbar}
      subHead={tabsBar}
      formSlot={
        creating ? (
          <NewContactForm
            actorName={actorName}
            onDone={(msg) => {
              toast(msg);
              setCreating(false);
            }}
            onCreated={(clientNo) => {
              setCreating(false);
              navigate(`/contacts/${encodeURIComponent(clientNo)}`);
            }}
          />
        ) : undefined
      }
    >
      {/* Reconcile preview — auditable two-step (dryRun plan → confirm apply). */}
      {reconcilePlan && (
        <div
          style={{
            background: 'var(--surface-1, #fff)',
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            padding: '14px 16px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <b style={{ fontSize: 14 }}>Reconcile duplicate contacts</b>
            <span style={{ color: 'var(--ink-mute)' }}>
              {reconcilePlan.scannedDupes} astro-sync shadow
              {reconcilePlan.scannedDupes === 1 ? '' : 's'} scanned
            </span>
          </div>

          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', margin: '10px 0' }}>
            <span>
              <b style={{ color: 'var(--grn, #2e7d32)' }}>{reconcilePlan.counts.willMerge}</b>{' '}
              will merge
            </span>
            <span>
              <b>{reconcilePlan.counts.willKeep}</b> will keep
            </span>
            <span>
              <b style={{ color: 'var(--warn-border, #b8860b)' }}>
                {reconcilePlan.counts.needsReview}
              </b>{' '}
              need review
            </span>
          </div>

          {reconcilePlan.merges.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ color: 'var(--ink-soft)', marginBottom: 4 }}>
                Will merge (shadow → canonical):
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {reconcilePlan.merges.map((m) => (
                  <li key={m.dupeId} style={{ marginBottom: 2 }}>
                    {m.dupeName} ({m.dupeId}) → <b>{m.canonicalName}</b> ({m.canonicalId})
                    <span style={{ color: 'var(--ink-mute)', fontSize: 12 }}>
                      {' '}
                      · {m.reason}
                      {m.groupUpdates.length > 0 &&
                        ` · ${m.groupUpdates.length} group${m.groupUpdates.length === 1 ? '' : 's'}`}
                      {m.projectLinkedUpdates.length > 0 &&
                        ` · ${m.projectLinkedUpdates.length} project${
                          m.projectLinkedUpdates.length === 1 ? '' : 's'
                        }`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {reconcilePlan.needsReview.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ color: 'var(--ink-soft)', marginBottom: 4 }}>
                Needs review (not merged — confirm manually):
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {reconcilePlan.needsReview.map((r) => (
                  <li key={r.dupeId} style={{ marginBottom: 2 }}>
                    {r.dupeName} ({r.dupeId})
                    {r.candidateName && (
                      <>
                        {' '}
                        ↔ {r.candidateName} ({r.candidateId})
                      </>
                    )}
                    <span style={{ color: 'var(--ink-mute)', fontSize: 12 }}> · {r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {reconcilePlan.warnings.clientIdRefs.length > 0 && (
            <div
              style={{
                marginTop: 10,
                color: 'var(--warn-border, #b8860b)',
                fontSize: 12,
              }}
            >
              ⚠ {reconcilePlan.warnings.clientIdRefs.length} shadow reference
              {reconcilePlan.warnings.clientIdRefs.length === 1 ? '' : 's'} found as a project's
              primary client — these are NOT auto-repointed. Review before applying.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <Btn3
              intent="grn"
              size="sm"
              onClick={applyReconcile}
              disabled={reconcileBusy || reconcilePlan.counts.willMerge === 0}
            >
              {reconcileBusy
                ? 'Applying…'
                : `Apply ${reconcilePlan.counts.willMerge} merge${
                    reconcilePlan.counts.willMerge === 1 ? '' : 's'
                  }`}
            </Btn3>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => setReconcilePlan(null)}
              disabled={reconcileBusy}
            >
              Cancel
            </Btn3>
          </div>
        </div>
      )}

      {/* ── Body — the ONLY scroll region (the ListScreen's .list-scroll owns
             it and flexes to fill the viewport; toolbar + tabs are pinned
             above). The active tab decides WHAT renders: person cards, group
             cards, or the Duplicates panel. No inner overflow box anywhere. ── */}
      <>
        {loading && <ListLoading label="Loading contacts…" />}

        {/* ── PERSON tabs: All / Clients / Professionals ── */}
        {!loading && PERSON_TABS.has(activeTab) && (
          <>
            <PersonTabContext tab={activeTab} />
            {list.length === 0 ? (
              <EmptyNote title={query ? 'No matches' : 'No contacts yet'}>
                {query
                  ? `No contact matches "${query}". Clear the search to see all contacts.`
                  : activeTab === 'all'
                    ? 'Convert a quoted lead from the Lead Inbox, or add a standalone contact above.'
                    : `No ${TAB_LABEL_MAP[activeTab]} yet. Switch to "All" to see everyone.`}
              </EmptyNote>
            ) : (
              <div className="cd-list">
                {list.map((c) => (
                  <PersonCard
                    key={c.clientId}
                    row={c}
                    master={mastersById.get(c.clientId)}
                    isClient={contactStatus(c.clientId, allProjects) === 'client'}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── GROUP tabs: Family / Organisations — GROUP cards, not people ── */}
        {!loading && activeTab === 'family' && (
          <GroupTabPanel
            kind="family"
            groups={familyGroupsList}
            query={query}
            onOpen={(id) => navigate(`/families/${encodeURIComponent(id)}`)}
          />
        )}
        {!loading && activeTab === 'organisations' && (
          <GroupTabPanel
            kind="org"
            groups={orgGroupsList}
            query={query}
            onOpen={(id) => navigate(`/families/${encodeURIComponent(id)}`)}
          />
        )}

        {/* ── DUPLICATES tab ── */}
        {!loading && activeTab === 'duplicates' && (
          <DuplicatesPanel
            personPairs={personDupPairs}
            groupPairs={groupDupPairs}
            allProjects={allProjects}
            actorName={actorName}
            groupNamesForContact={groupNamesForContact}
            onToast={toast}
            onOpenGroup={(id) => navigate(`/families/${encodeURIComponent(id)}`)}
          />
        )}
      </>
    </ListScreen>
  );
}

/* ════════════════════ Tab context lines ════════════════════ */

function PersonTabContext({ tab }: { tab: ContactTab }) {
  const text: Record<string, React.ReactNode> = {
    all: (
      <>
        Everyone in the directory.{' '}
        <b>Families and Organisations appear as group cards under their own tabs</b> — this
        list shows the individual people.
      </>
    ),
    clients: (
      <>
        People with at least one live or completed project. <b>Person</b> records — tap to
        open the contact.
      </>
    ),
    professionals: (
      <>
        Architects, designers, vendors and consultants who serve on projects. <b>Person</b>{' '}
        records — tap to open the contact.
      </>
    ),
  };
  const body = text[tab];
  if (!body) return null;
  return <p className="cd-context">{body}</p>;
}

/* ════════════════════ Person card ════════════════════ */

function PersonCard({
  row,
  master,
  isClient,
}: {
  row: ClientRow & { roles: Set<P1LensRole> };
  master?: LiveClient;
  isClient: boolean;
}) {
  const phone = master ? primaryPhone(master) : '';
  const email = master ? primaryEmail(master) : '';
  const badges = lensBadges(row.roles);
  // Pill: Client status wins (shared contactStatus lens); else the first
  // reconciled role badge; else a neutral "Contact".
  const pill = isClient
    ? { cls: 'client', label: 'Client' }
    : badges.length > 0
      ? { cls: 'prof', label: badges[0] }
      : { cls: '', label: 'Contact' };
  return (
    <Link
      to={`/contacts/${encodeURIComponent(row.clientId)}`}
      className="cd-card"
    >
      <span className="cd-avatar" aria-hidden="true">
        {initials(row.name)}
      </span>
      <span className="cd-body">
        <span className="cd-name-line">
          <span className="cd-name">{row.name}</span>
          <span className={`cd-pill ${pill.cls}`}>{pill.label}</span>
          {/* Contact number — muted, for staff reference (owner request). */}
          {row.clientNo && <span className="cd-cno">{row.clientNo}</span>}
        </span>
        <div className="cd-sub">
          {phone}
          {phone && email && ' · '}
          {email}
          {/* Project count + revenue — only on client rows (contact identity
              over deal value for non-client lenses). */}
          {isClient && (
            <>
              {(phone || email) && ' · '}
              <b>
                {row.projectCount} project{row.projectCount === 1 ? '' : 's'}
              </b>
              {row.totalValueINR > 0 && (
                <>
                  {' · '}
                  <span className="cd-money">
                    <b>{inr(row.totalValueINR)}</b> paid
                  </span>
                </>
              )}
            </>
          )}
        </div>
      </span>
      <span className="cd-chev" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

/* ════════════════════ Group tab panel (Family / Organisations) ════════════════════ */

function GroupTabPanel({
  kind,
  groups,
  query,
  onOpen,
}: {
  kind: 'family' | 'org';
  groups: FamilyGroup[];
  query: string;
  onOpen: (id: string) => void;
}) {
  const filtered = query
    ? groups.filter(
        (g) =>
          g.name.toLowerCase().includes(query.toLowerCase()) ||
          g.members.some((m) => m.name.toLowerCase().includes(query.toLowerCase())),
      )
    : groups;
  const isFam = kind === 'family';
  return (
    <>
      <p className="cd-context">
        These are{' '}
        <b>{isFam ? 'family groups' : 'organisations'}</b>, not individual people. Each card
        is one {isFam ? 'family' : 'company / body'} — tap to open the{' '}
        <b>{isFam ? 'Family page' : 'Organisation page'}</b> and its {isFam ? 'members' : 'key personnel'}.
      </p>
      {filtered.length === 0 ? (
        <EmptyNote title={query ? 'No matches' : isFam ? 'No family groups yet' : 'No organisations yet'}>
          {query
            ? `No ${isFam ? 'family' : 'organisation'} matches "${query}".`
            : `Create a group under Manage → Groups to see it here.`}
        </EmptyNote>
      ) : (
        <div className="cd-list">
          {filtered.map((g) => (
            <GroupCard key={g.id} kind={kind} group={g} onOpen={onOpen} />
          ))}
        </div>
      )}
    </>
  );
}

function GroupCard({
  kind,
  group,
  onOpen,
}: {
  kind: 'family' | 'org';
  group: FamilyGroup;
  onOpen: (id: string) => void;
}) {
  const isFam = kind === 'family';
  const count = group.members.length;
  const head = group.members[0]?.name;
  const rest = group.members.slice(1, 4).map((m) => m.name);
  const hasGst = Boolean((group.gstNumber ?? '').trim());
  return (
    <button
      type="button"
      className="cd-card"
      onClick={() => onOpen(group.id)}
      aria-label={`Open ${group.name}`}
    >
      <span className={`cd-tile ${isFam ? 'fam' : 'org'}`} aria-hidden="true">
        {isFam ? '👪' : '🏢'}
      </span>
      <span className="cd-body">
        <span className="cd-name-line">
          <span className="cd-name">{group.name}</span>
          <span className={`cd-group-label ${isFam ? 'fam' : 'org'}`}>
            {isFam ? 'Family' : 'Organisation'}
          </span>
        </span>
        <div className="cd-sub">
          <span className="cd-member-count">
            <span aria-hidden="true">👥</span> {count}{' '}
            {isFam ? (count === 1 ? 'member' : 'members') : count === 1 ? 'key person' : 'key personnel'}
          </span>
          {head && (
            <>
              {' · '}
              {isFam ? 'Head' : 'Key'}: <b>{head}</b>
              {rest.length > 0 && ` · also ${rest.join(', ')}`}
            </>
          )}
          {!isFam && (
            <>
              {' · '}
              {hasGst ? 'GST on file' : 'no GST'}
            </>
          )}
        </div>
      </span>
      <span className="cd-chev" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/* ════════════════════ Duplicates panel ════════════════════ */

/** PURE — union phones from any number of contacts, deduped by phone key. */
function mergePhones(
  ...clients: Array<LiveClient | null>
): Array<{ number: string; label?: string; whatsApp?: boolean }> {
  const out: Array<{ number: string; label?: string; whatsApp?: boolean }> = [];
  const seen = new Set<string>();
  for (const c of clients) {
    if (!c) continue;
    const arr = [
      ...(c.phones ?? []),
      ...(c.phone ? [{ number: c.phone, label: 'primary', whatsApp: true }] : []),
    ];
    for (const p of arr) {
      const k = phoneKey(p.number);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(p);
    }
  }
  return out;
}
/** PURE — union emails, deduped by email key. */
function mergeEmails(
  ...clients: Array<LiveClient | null>
): Array<{ address: string; label?: string }> {
  const out: Array<{ address: string; label?: string }> = [];
  const seen = new Set<string>();
  for (const c of clients) {
    if (!c) continue;
    const arr = [...(c.emails ?? []), ...(c.email ? [{ address: c.email, label: 'primary' }] : [])];
    for (const e of arr) {
      const k = emailKey(e.address);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(e);
    }
  }
  return out;
}
/** Richness score — more filled person fields ⇒ better default survivor. */
function richness(c: LiveClient): number {
  let n = 0;
  if ((c.phones?.length ?? 0) + (c.phone ? 1 : 0) > 0) n++;
  if ((c.emails?.length ?? 0) + (c.email ? 1 : 0) > 0) n++;
  if (c.dob) n++;
  if (c.tob) n++;
  if (c.pob) n++;
  if (c.prakriti) n++;
  if (c.address) n++;
  if (c.referredBy) n++;
  if ((c.familyIds?.length ?? 0) > 0) n++;
  return n;
}

function DuplicatesPanel({
  personPairs,
  groupPairs,
  allProjects,
  actorName,
  groupNamesForContact,
  onToast,
  onOpenGroup,
}: {
  personPairs: PersonDupPair[];
  groupPairs: GroupDupPair[];
  allProjects: LiveProject[];
  actorName: string;
  groupNamesForContact: (cid: string) => string;
  onToast: (msg: string) => void;
  onOpenGroup: (id: string) => void;
}) {
  const total = personPairs.length + groupPairs.length;
  return (
    <>
      <p className="cd-context">
        Suspected duplicate records the system flagged — across{' '}
        <b>clients, families, professionals and organisations</b>. Review each set, then{' '}
        <b>delete one</b>, <b>merge</b>, or mark them <b>not a duplicate</b>. Nothing changes
        until you confirm.
      </p>
      {total === 0 ? (
        <EmptyNote title="No duplicates flagged">
          Nothing looks like a duplicate right now. When two records share a phone, email,
          name or GSTIN they will appear here to review.
        </EmptyNote>
      ) : (
        <div className="cd-list">
          {personPairs.map((p) => (
            <PersonDupCard
              key={`${p.a.cid}::${p.b.cid}`}
              pair={p}
              allProjects={allProjects}
              actorName={actorName}
              groupNamesForContact={groupNamesForContact}
              onToast={onToast}
            />
          ))}
          {groupPairs.map((p) => (
            <GroupDupCard
              key={`${p.a.id}::${p.b.id}`}
              pair={p}
              actorName={actorName}
              onToast={onToast}
              onOpenGroup={onOpenGroup}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* ──────── person duplicate cluster card + inline resolve ──────── */

interface CmpRow { label: string; a: string; b: string; match: boolean }

function CompareGrid({ recA, recB, rows }: { recA: string; recB: string; rows: CmpRow[] }) {
  return (
    <>
      <div className="cd-cmp">
        <div className="cd-cmp-colhead">
          <span className="cd-cmp-rec">{recA}</span>
        </div>
        <div className="cd-cmp-colhead">
          <span className="cd-cmp-rec">{recB}</span>
        </div>
      </div>
      <div className="cd-cmp-grid">
        {rows.map((r) => (
          <div key={r.label} className={`cd-cmp-row ${r.match ? 'match' : ''}`}>
            <div className="cd-cmp-field">{r.label}</div>
            <div className={`cd-cmp-val ${r.a ? '' : 'empty'}`}>{r.a || '— not on record —'}</div>
            <div className={`cd-cmp-val diff ${r.b ? '' : 'empty'}`}>{r.b || '— not on record —'}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function roleStatusText(c: LiveClient, allProjects: LiveProject[]): string {
  const status = contactStatus(c.cid, allProjects) === 'client' ? 'Client' : 'Contact';
  const tags = (c.roleTags ?? []).join(', ');
  return tags ? `${status} · ${tags}` : status;
}

function PersonDupCard({
  pair,
  allProjects,
  actorName,
  groupNamesForContact,
  onToast,
}: {
  pair: PersonDupPair;
  allProjects: LiveProject[];
  actorName: string;
  groupNamesForContact: (cid: string) => string;
  onToast: (msg: string) => void;
}) {
  const { a, b, reasons } = pair;
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<'compare' | 'merge'>('compare');
  const [busy, setBusy] = useState(false);

  // Default survivor: older record, tiebroken by richness.
  const olderIsA =
    (a.createdAt ?? '9999') !== (b.createdAt ?? '9999')
      ? (a.createdAt ?? '9999') < (b.createdAt ?? '9999')
      : richness(a) >= richness(b);
  const defSurvivor: 'a' | 'b' = olderIsA ? 'a' : 'b';

  // per-field default = survivor side if it has a value, else the other side.
  const has = (c: LiveClient, f: (x: LiveClient) => unknown) => Boolean(f(c) && String(f(c)).trim());
  const dflt = (f: (x: LiveClient) => unknown): 'a' | 'b' =>
    has(defSurvivor === 'a' ? a : b, f) ? defSurvivor : has(defSurvivor === 'a' ? b : a, f) ? (defSurvivor === 'a' ? 'b' : 'a') : defSurvivor;

  const [pick, setPick] = useState({
    survivor: defSurvivor,
    name: dflt((c) => c.name),
    birth: dflt(fmtBirth),
    prakriti: dflt((c) => c.prakriti),
    address: dflt(fmtAddress),
    ref: dflt((c) => c.referredBy?.name),
    role: dflt((c) => (c.roleTags ?? []).join(',')),
    keepAPhones: true,
    keepBPhones: true,
    keepAEmails: true,
    keepBEmails: true,
    keepGroups: true,
  });

  const phoneMatch = allPhoneKeys(a).some((p) => allPhoneKeys(b).includes(p));
  const emailMatch = allEmailKeys(a).some((e) => allEmailKeys(b).includes(e));
  const eq = (x: string, y: string) => Boolean(x) && x.trim().toLowerCase() === y.trim().toLowerCase();

  const rows: CmpRow[] = [
    { label: 'Name', a: a.name, b: b.name, match: eq(a.name, b.name) },
    { label: 'Phone', a: fmtPhones(a), b: fmtPhones(b), match: phoneMatch },
    { label: 'Email', a: fmtEmails(a), b: fmtEmails(b), match: emailMatch },
    { label: 'DOB / TOB / POB', a: fmtBirth(a), b: fmtBirth(b), match: eq(fmtBirth(a), fmtBirth(b)) },
    { label: 'Prakriti', a: a.prakriti ?? '', b: b.prakriti ?? '', match: eq(a.prakriti ?? '', b.prakriti ?? '') },
    { label: 'Address', a: fmtAddress(a), b: fmtAddress(b), match: eq(fmtAddress(a), fmtAddress(b)) },
    { label: 'Referred by', a: a.referredBy?.name ?? '', b: b.referredBy?.name ?? '', match: eq(a.referredBy?.name ?? '', b.referredBy?.name ?? '') },
    { label: 'Groups', a: groupNamesForContact(a.cid), b: groupNamesForContact(b.cid), match: eq(groupNamesForContact(a.cid), groupNamesForContact(b.cid)) },
    { label: 'Role / status', a: roleStatusText(a, allProjects), b: roleStatusText(b, allProjects), match: eq(roleStatusText(a, allProjects), roleStatusText(b, allProjects)) },
  ];

  async function softDelete(loser: LiveClient) {
    if (busy) return;
    setBusy(true);
    try {
      await trashClient(loser.cid, actorName, true, allProjects);
      onToast(`Soft-deleted ${loser.clientNo} — recoverable from Trash for 30 days.`);
      // listener refresh drops this pair from the list.
    } catch (e) {
      onToast(`Couldn't delete ${loser.clientNo}: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  async function keepBoth() {
    if (busy) return;
    setBusy(true);
    try {
      await markContactsNotDuplicate(a.cid, b.cid, actorName);
      onToast('Marked as not a duplicate — this pair won’t be flagged again.');
    } catch (e) {
      onToast(`Couldn't save: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  async function doMerge() {
    if (busy) return;
    setBusy(true);
    const S = pick.survivor === 'a' ? a : b;
    const L = pick.survivor === 'a' ? b : a;
    const nameSrc = pick.name === 'a' ? a : b;
    const birthSrc = pick.birth === 'a' ? a : b;
    const prakSrc = pick.prakriti === 'a' ? a : b;
    const addrSrc = pick.address === 'a' ? a : b;
    const refSrc = pick.ref === 'a' ? a : b;
    const roleSrc = pick.role === 'a' ? a : b;
    try {
      // 1. Name (only if it changes).
      if (nameSrc.name && nameSrc.name !== S.name) {
        await updateClient(S.cid, { name: nameSrc.name }, actorName);
      }
      // 2. Phones / emails — union of the checked sides.
      const phones = mergePhones(pick.keepAPhones ? a : null, pick.keepBPhones ? b : null);
      const emails = mergeEmails(pick.keepAEmails ? a : null, pick.keepBEmails ? b : null);
      if (phones.length) await savePhones(S.cid, phones);
      if (emails.length) await saveEmails(S.cid, emails);
      // 3. Person data — the chosen single-value fields.
      await savePersonData(S.cid, {
        ...(birthSrc.dob ? { dob: birthSrc.dob } : {}),
        ...(birthSrc.tob ? { tob: birthSrc.tob } : {}),
        ...(birthSrc.pob ? { pob: birthSrc.pob } : {}),
        ...(prakSrc.prakriti ? { prakriti: prakSrc.prakriti } : {}),
        ...(addrSrc.address ? { address: addrSrc.address } : {}),
        ...(refSrc.referredBy ? { referredBy: refSrc.referredBy } : {}),
      });
      // 4. Roles — the chosen side's tags.
      if (roleSrc.roleTags && roleSrc.roleTags.length) {
        await saveRoleTags(S.cid, roleSrc.roleTags);
      }
      // 5. Re-point every project reference (participants / linked contacts /
      //    primary client / partner) AND the loser's group memberships onto the
      //    survivor, then soft-trash the loser — ALL in one atomic batch. This
      //    is what lets a project-linked loser be trashed cleanly (no
      //    clientTrashBlock): its work moves to the survivor first. `moveGroups`
      //    honours the field-by-field "move group memberships" pick.
      const repoint = await mergeContactInto(L.cid, S.cid, actorName, {
        survivorName: nameSrc.name || S.name,
        moveGroups: pick.keepGroups,
      });
      const proj = repoint.projectsRepointed;
      onToast(
        `Merged ${L.clientNo} into ${S.clientNo} — ${L.clientNo} moved to Trash (recoverable)` +
          `${proj ? `, ${proj} project${proj === 1 ? '' : 's'} re-pointed` : ''}.`,
      );
    } catch (e) {
      onToast(
        `Merged the chosen fields into ${S.clientNo}, but couldn't move ${L.clientNo}'s ` +
          `references / trash it: ${(e as Error).message}. Nothing was orphaned — re-run the merge.`,
      );
      setBusy(false);
    }
  }

  const recA = `C · ${a.clientNo}`;
  const recB = `C · ${b.clientNo}`;

  return (
    <div className={`cd-dup-cluster ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="cd-dup-head"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
          setStage('compare');
        }}
      >
        <span className="cd-dup-icon" aria-hidden="true">⧉</span>
        <span className="cd-dup-headbody">
          <span className="cd-dup-name-line">
            <span className="cd-dup-name">{a.name}</span>
            <span className="cd-dup-count">2 records</span>
          </span>
          <span className="cd-dup-why">
            <span aria-hidden="true">🔗</span> Flagged: <b>{reasons.map((r) => r.label).join(' · ')}</b>
          </span>
          <span className="cd-dup-snips">
            <span className="cd-dup-snip"><b>{a.clientNo}</b> · {a.name}</span>
            <span className="cd-dup-snip"><b>{b.clientNo}</b> · {b.name}</span>
          </span>
        </span>
        <span className="cd-dup-cta">
          <span className="cd-dup-cta-lbl">Review &amp; resolve</span>
          <span className="cd-dup-chev" aria-hidden="true">›</span>
        </span>
      </button>

      {open && (
        <div className="cd-resolve">
          <div className="cd-resolve-head">
            <div className="cd-resolve-title">Resolve this duplicate</div>
            <div className="cd-resolve-legend">
              <span className="cd-legend-swatch" aria-hidden="true" /> highlighted rows ={' '}
              fields that <b>match</b> across both records
            </div>
          </div>

          {stage === 'compare' ? (
            <>
              <CompareGrid recA={recA} recB={recB} rows={rows} />
              <div className="cd-resolve-actions">
                <div className="cd-ra-group">
                  <div className="cd-ra-label">Keep just one, discard the other</div>
                  <div className="cd-ra-btns">
                    <button className="cd-btn danger" disabled={busy} onClick={() => softDelete(b)}>
                      Delete {b.clientNo}
                    </button>
                    <button className="cd-btn danger" disabled={busy} onClick={() => softDelete(a)}>
                      Delete {a.clientNo}
                    </button>
                  </div>
                  <div className="cd-ra-note">Soft-delete — recoverable from Trash for 30 days.</div>
                </div>
                <div className="cd-ra-group">
                  <div className="cd-ra-label">Combine both into one record</div>
                  <div className="cd-ra-btns">
                    <button className="cd-btn primary" disabled={busy} onClick={() => setStage('merge')}>
                      Merge these two →
                    </button>
                  </div>
                  <div className="cd-ra-note">Pick which value wins for each field, field by field.</div>
                </div>
                <div className="cd-ra-group">
                  <div className="cd-ra-label">These are two different people</div>
                  <div className="cd-ra-btns">
                    <button className="cd-btn neutral" disabled={busy} onClick={keepBoth}>
                      Not a duplicate — keep both
                    </button>
                  </div>
                  <div className="cd-ra-note">
                    Both records stay untouched and this pair is never flagged again.
                  </div>
                </div>
              </div>
            </>
          ) : (
            <MergePicker
              a={a}
              b={b}
              pick={pick}
              setPick={setPick}
              busy={busy}
              onBack={() => setStage('compare')}
              onMerge={doMerge}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ──────── field-by-field merge picker ──────── */

type MergePick = {
  survivor: 'a' | 'b';
  name: 'a' | 'b';
  birth: 'a' | 'b';
  prakriti: 'a' | 'b';
  address: 'a' | 'b';
  ref: 'a' | 'b';
  role: 'a' | 'b';
  keepAPhones: boolean;
  keepBPhones: boolean;
  keepAEmails: boolean;
  keepBEmails: boolean;
  keepGroups: boolean;
};

function RadioRow({
  label,
  name,
  valA,
  valB,
  pick,
  onPick,
}: {
  label: string;
  /** DOM radio-group name — MUST be unique per card so pickers don't collide. */
  name: string;
  valA: string;
  valB: string;
  pick: 'a' | 'b';
  onPick: (v: 'a' | 'b') => void;
}) {
  const aEmpty = !valA.trim();
  const bEmpty = !valB.trim();
  return (
    <div className="cd-mp-row">
      <div className="cd-mp-field">{label}</div>
      <label className={`cd-mp-opt ${aEmpty ? 'disabled' : ''}`}>
        <input type="radio" name={name} checked={pick === 'a'} disabled={aEmpty} onChange={() => onPick('a')} />
        <span className={aEmpty ? 'cd-mp-empty' : ''}>{valA || '— not on record —'}</span>
      </label>
      <label className={`cd-mp-opt ${bEmpty ? 'disabled' : ''}`}>
        <input type="radio" name={name} checked={pick === 'b'} disabled={bEmpty} onChange={() => onPick('b')} />
        <span className={bEmpty ? 'cd-mp-empty' : ''}>{valB || '— not on record —'}</span>
      </label>
    </div>
  );
}

function MergePicker({
  a,
  b,
  pick,
  setPick,
  busy,
  onBack,
  onMerge,
}: {
  a: LiveClient;
  b: LiveClient;
  pick: MergePick;
  setPick: React.Dispatch<React.SetStateAction<MergePick>>;
  busy: boolean;
  onBack: () => void;
  onMerge: () => void;
}) {
  const set = (patch: Partial<MergePick>) => setPick((p) => ({ ...p, ...patch }));
  // Unique radio-group namespace per card so two open pickers never collide.
  const uid = `${a.cid}-${b.cid}`;
  return (
    <div className="merge-stage">
      <div className="cd-merge-intro">
        <button type="button" className="cd-merge-back" onClick={onBack}>
          ‹ Back to compare
        </button>
        <span className="cd-merge-help">
          For each field, choose which record’s value survives. Multi-value fields (phones,
          emails, groups) can <b>keep both</b>.
        </span>
      </div>

      <div className="cd-mp-grid">
        <div className="cd-mp-heads">
          <div className="cd-mp-fieldhead">Field</div>
          <div className="cd-mp-opthead">From <b>{a.clientNo}</b></div>
          <div className="cd-mp-opthead">From <b>{b.clientNo}</b></div>
        </div>

        <RadioRow label="Name" name={`${uid}-name`} valA={a.name} valB={b.name} pick={pick.name} onPick={(v) => set({ name: v })} />

        {/* phones — keep both */}
        <div className="cd-mp-row">
          <div className="cd-mp-field">Phones <span className="cd-mp-multi">multi</span></div>
          <label className="cd-mp-opt keepboth">
            <input type="checkbox" checked={pick.keepAPhones} onChange={(e) => set({ keepAPhones: e.target.checked })} />
            <span>{fmtPhones(a) || '— none —'}</span>
          </label>
          <label className="cd-mp-opt keepboth">
            <input type="checkbox" checked={pick.keepBPhones} onChange={(e) => set({ keepBPhones: e.target.checked })} />
            <span>{fmtPhones(b) || '— none —'}</span>
          </label>
        </div>

        {/* emails — keep both */}
        <div className="cd-mp-row">
          <div className="cd-mp-field">Emails <span className="cd-mp-multi">multi</span></div>
          <label className="cd-mp-opt keepboth">
            <input type="checkbox" checked={pick.keepAEmails} onChange={(e) => set({ keepAEmails: e.target.checked })} />
            <span>{fmtEmails(a) || '— none —'}</span>
          </label>
          <label className="cd-mp-opt keepboth">
            <input type="checkbox" checked={pick.keepBEmails} onChange={(e) => set({ keepBEmails: e.target.checked })} />
            <span>{fmtEmails(b) || '— none —'}</span>
          </label>
        </div>

        <RadioRow label="DOB / TOB / POB" name={`${uid}-birth`} valA={fmtBirth(a)} valB={fmtBirth(b)} pick={pick.birth} onPick={(v) => set({ birth: v })} />
        <RadioRow label="Prakriti" name={`${uid}-prakriti`} valA={a.prakriti ?? ''} valB={b.prakriti ?? ''} pick={pick.prakriti} onPick={(v) => set({ prakriti: v })} />
        <RadioRow label="Address" name={`${uid}-address`} valA={fmtAddress(a)} valB={fmtAddress(b)} pick={pick.address} onPick={(v) => set({ address: v })} />
        <RadioRow label="Referred by" name={`${uid}-ref`} valA={a.referredBy?.name ?? ''} valB={b.referredBy?.name ?? ''} pick={pick.ref} onPick={(v) => set({ ref: v })} />

        {/* groups — keep both */}
        <div className="cd-mp-row">
          <div className="cd-mp-field">Group memberships <span className="cd-mp-multi">multi</span></div>
          <label className="cd-mp-opt keepboth" style={{ gridColumn: '2 / span 2' }}>
            <input type="checkbox" checked={pick.keepGroups} onChange={(e) => set({ keepGroups: e.target.checked })} />
            <span>Move the discarded record’s group memberships onto the survivor</span>
          </label>
        </div>

        <RadioRow label="Role / status" name={`${uid}-role`} valA={(a.roleTags ?? []).join(', ')} valB={(b.roleTags ?? []).join(', ')} pick={pick.role} onPick={(v) => set({ role: v })} />
      </div>

      <div className="cd-merge-survivor">
        <div>
          <div className="cd-ms-label">Merge into survivor</div>
          <div className="cd-ms-pick">
            Survivor record (keeps the C-number &amp; history):
            <label className="cd-ms-radio">
              <input type="radio" name={`${uid}-survivor`} checked={pick.survivor === 'a'} onChange={() => set({ survivor: 'a' })} />
              {a.clientNo}
            </label>
            <label className="cd-ms-radio">
              <input type="radio" name={`${uid}-survivor`} checked={pick.survivor === 'b'} onChange={() => set({ survivor: 'b' })} />
              {b.clientNo}
            </label>
          </div>
        </div>
        <button className="cd-btn primary" disabled={busy} onClick={onMerge}>
          {busy ? 'Merging…' : 'Merge into survivor'}
        </button>
      </div>
    </div>
  );
}

/* ──────── group duplicate cluster card ──────── */

function GroupDupCard({
  pair,
  actorName,
  onToast,
  onOpenGroup,
}: {
  pair: GroupDupPair;
  actorName: string;
  onToast: (msg: string) => void;
  onOpenGroup: (id: string) => void;
}) {
  const { a, b, reasons, kind } = pair;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isOrg = kind === 'org';

  const memberNames = (g: FamilyGroup) => g.members.map((m) => m.name).join(', ');
  const eq = (x: string, y: string) => Boolean(x) && x.trim().toLowerCase() === y.trim().toLowerCase();
  const rows: CmpRow[] = [
    { label: 'Name', a: a.name, b: b.name, match: eq(a.name, b.name) },
    { label: 'Type', a: isOrg ? 'Organisation' : 'Family', b: isOrg ? 'Organisation' : 'Family', match: true },
    ...(isOrg
      ? [{ label: 'GSTIN', a: a.gstNumber ?? '', b: b.gstNumber ?? '', match: eq(a.gstNumber ?? '', b.gstNumber ?? '') }]
      : []),
    { label: isOrg ? 'Key personnel' : 'Members', a: `${a.members.length} · ${memberNames(a)}`, b: `${b.members.length} · ${memberNames(b)}`, match: false },
    ...(isOrg
      ? [{ label: 'Billing address', a: a.billingAddress ?? '', b: b.billingAddress ?? '', match: eq(a.billingAddress ?? '', b.billingAddress ?? '') }]
      : []),
  ];

  async function keepBoth() {
    if (busy) return;
    setBusy(true);
    try {
      await markGroupsNotDuplicate(a.id, b.id, actorName);
      onToast('Marked as not a duplicate — these two groups won’t be flagged again.');
    } catch (e) {
      onToast(`Couldn't save: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  // Recoverable soft-delete of the discarded group (never a hard delete).
  async function softDelete(loser: FamilyGroup) {
    if (busy) return;
    setBusy(true);
    try {
      await trashFamily(loser.id, actorName, true);
      onToast(`Soft-deleted “${loser.name}” — recoverable from Trash for 30 days.`);
      // listener refresh (trashed groups are filtered) drops this pair.
    } catch (e) {
      onToast(`Couldn't delete “${loser.name}”: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  // Merge the loser group INTO the survivor: union members, back-fill the
  // survivor's empty billing, re-point project links, then soft-trash the loser.
  async function doMerge(survivor: FamilyGroup, loser: FamilyGroup) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await mergeFamilyInto(loser.id, survivor.id, actorName);
      const moved = res.membersAdded;
      const proj = res.projectsRepointed;
      onToast(
        `Merged “${loser.name}” into “${survivor.name}” — ${moved} member${moved === 1 ? '' : 's'} moved` +
          `${proj ? `, ${proj} project${proj === 1 ? '' : 's'} re-pointed` : ''}; ` +
          `“${loser.name}” moved to Trash (recoverable).`,
      );
    } catch (e) {
      onToast(`Couldn't merge: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  return (
    <div className={`cd-dup-cluster ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="cd-dup-head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`cd-dup-icon ${isOrg ? 'org' : 'fam'}`} aria-hidden="true">
          {isOrg ? '🏢' : '👪'}
        </span>
        <span className="cd-dup-headbody">
          <span className="cd-dup-name-line">
            <span className="cd-dup-name">{a.name}</span>
            <span className="cd-dup-count">2 {isOrg ? 'organisations' : 'families'}</span>
          </span>
          <span className="cd-dup-why">
            <span aria-hidden="true">🔗</span> Flagged: <b>{reasons.map((r) => r.label).join(' · ')}</b>
          </span>
          <span className="cd-dup-snips">
            <span className="cd-dup-snip">{a.name} · {a.members.length} members</span>
            <span className="cd-dup-snip">{b.name} · {b.members.length} members</span>
          </span>
        </span>
        <span className="cd-dup-cta">
          <span className="cd-dup-cta-lbl">Review &amp; resolve</span>
          <span className="cd-dup-chev" aria-hidden="true">›</span>
        </span>
      </button>

      {open && (
        <div className="cd-resolve">
          <div className="cd-resolve-head">
            <div className="cd-resolve-title">Resolve this duplicate</div>
            <div className="cd-resolve-legend">
              <span className="cd-legend-swatch" aria-hidden="true" /> highlighted rows ={' '}
              fields that <b>match</b>
            </div>
          </div>
          <CompareGrid recA={a.name} recB={b.name} rows={rows} />
          <div className="cd-resolve-actions">
            <div className="cd-ra-group">
              <div className="cd-ra-label">These are two different {isOrg ? 'organisations' : 'families'}</div>
              <div className="cd-ra-btns">
                <button className="cd-btn neutral" disabled={busy} onClick={keepBoth}>
                  Not a duplicate — keep both
                </button>
              </div>
              <div className="cd-ra-note">
                Both groups stay untouched and this pair is never flagged again.
              </div>
            </div>
            <div className="cd-ra-group">
              <div className="cd-ra-label">Keep just one, discard the other</div>
              <div className="cd-ra-btns">
                <button className="cd-btn danger" disabled={busy} onClick={() => softDelete(b)}>
                  Delete {b.name}
                </button>
                <button className="cd-btn danger" disabled={busy} onClick={() => softDelete(a)}>
                  Delete {a.name}
                </button>
              </div>
              <div className="cd-ra-note">Soft-delete — recoverable from Trash for 30 days.</div>
            </div>
            <div className="cd-ra-group">
              <div className="cd-ra-label">
                Same {isOrg ? 'organisation' : 'family'} — combine into one
              </div>
              <div className="cd-ra-btns">
                <button className="cd-btn primary" disabled={busy} onClick={() => doMerge(a, b)}>
                  Merge into {a.name}
                </button>
                <button className="cd-btn primary" disabled={busy} onClick={() => doMerge(b, a)}>
                  Merge into {b.name}
                </button>
              </div>
              <div className="cd-ra-note">
                Moves the other {isOrg ? 'organisation' : 'family'}’s members onto the survivor
                {isOrg ? ' (empty billing fields are back-filled)' : ''}, re-points any linked
                projects, then soft-deletes the discarded group. Review first:
              </div>
              <div className="cd-ra-btns">
                <button className="cd-btn line" disabled={busy} onClick={() => onOpenGroup(a.id)}>
                  Open {a.name} in Groups
                </button>
                <button className="cd-btn line" disabled={busy} onClick={() => onOpenGroup(b.id)}>
                  Open {b.name} in Groups
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════ New Contact Form ════════════════════ */

function NewContactForm({
  actorName,
  onDone,
  onCreated,
}: {
  actorName: string;
  onDone: (msg: string) => void;
  onCreated: (clientNo: string) => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsApp, setWhatsApp] = useState(true);
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  // §6 — assign a role at create so the new contact lands in the right lens
  // (not just "All"). '' = a general Contact (Client is derived, never picked).
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    if (busy) return;
    if (!name.trim()) {
      onDone('Contact name is required.');
      return;
    }
    setBusy(true);
    try {
      const clientNo = await createClient(
        {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          location: location.trim(),
        },
        actorName,
      );
      // Persist the phone as a WhatsApp-flagged channel + the assigned role, so
      // the contact is complete and lens-correct the moment it opens.
      if (phone.trim()) {
        await savePhones(clientNo, [
          { number: phone.trim(), label: 'primary', whatsApp },
        ]);
      }
      if (role) {
        await saveRoleTags(clientNo, [role]);
      }
      onDone(`Created contact ${clientNo} (${name.trim()}) — opening it…`);
      onCreated(clientNo);
    } catch (e) {
      onDone(`Create failed: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  return (
    <Card flat style={{ marginBottom: 14 }}>
      <Lbl>New contact</Lbl>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Contact name (required)"
        style={inputStyle}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Location"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12.5,
            color: 'var(--ink-soft)',
            cursor: 'pointer',
          }}
          title="This number is used for WhatsApp"
        >
          <input
            type="checkbox"
            checked={whatsApp}
            onChange={(e) => setWhatsApp(e.target.checked)}
            disabled={!phone.trim()}
          />
          Phone is on WhatsApp
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12.5,
            color: 'var(--ink-soft)',
          }}
        >
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            aria-label="Assign a role at create"
            style={{
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r-sm)',
              padding: '7px 9px',
              fontSize: 13,
              fontFamily: 'inherit',
              color: 'var(--ink)',
              background: 'var(--surface)',
            }}
          >
            <option value="">General contact</option>
            {ASSIGNABLE_CONTACT_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '8px 0 0' }}>
        Birth details, prakriti, address and more emails are added on the contact
        record after it opens.
      </p>
      <div style={{ marginTop: 12 }}>
        <Btn3 intent="grn" size="sm" onClick={create} disabled={busy}>
          {busy ? 'Creating…' : 'Create contact'}
        </Btn3>
      </div>
    </Card>
  );
}
